import { useEffect } from 'react';
import { ActionIcon, Drawer, Group, Text } from '@mantine/core';
import { IconMinus, IconX } from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useUploadStore } from '../uploadStore';
import { useUppy } from '../useUppy';
import { useUploadCredentials } from '../useUploadCredentials';
import { useIngestcfgs } from '../useIngestcfgs';
import { useJobPolling } from '../useJobPolling';
import { UploadDropzone } from './UploadDropzone';
import { UploadFileList } from './UploadFileList';

export function UploadDrawer() {
  const { authData, selectedCId } = useAuth();
  const drawerOpen = useUploadStore((s) => s.drawerOpen);
  const closeDrawer = useUploadStore((s) => s.closeDrawer);
  const minimizeDrawer = useUploadStore((s) => s.minimizeDrawer);
  const items = useUploadStore((s) => s.items);
  const canIngest = Boolean(authData?.cpuser.permissions.ingest);
  const effectiveCId = canIngest ? selectedCId : null;

  const activeCount = items.filter(
    (i) => i.status === 'pending' || i.status === 'creating' || i.status === 'uploading' || i.status === 'processing'
  ).length;
  const inFlightCount = items.filter(
    (i) => i.status === 'pending' || i.status === 'creating' || i.status === 'uploading'
  ).length;

  // Hooks — kept alive via keepMounted
  const {
    ingestcfgs,
    selectedIngestcfgId,
    setSelectedIngestcfgId,
    hasMultiple,
    isRestricted,
  } = useIngestcfgs(effectiveCId);

  const selectedIngestcfg = ingestcfgs.find((c) => c.ingestcfgId === selectedIngestcfgId) ?? null;

  useUploadCredentials(effectiveCId);
  useJobPolling(effectiveCId);

  const { addFilesWithMeta, cancelUpload, cancelAll: _cancelAll, retryUpload } = useUppy(
    effectiveCId,
    selectedIngestcfg
  );

  // beforeunload guard — only while the client is still sending bytes;
  // once items reach server-side processing, closing the tab cancels nothing.
  useEffect(() => {
    if (!canIngest || inFlightCount === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [canIngest, inFlightCount > 0]);

  if (!canIngest || effectiveCId == null) {
    return null;
  }

  return (
    <Drawer
      opened={drawerOpen}
      onClose={closeDrawer}
      position="right"
      size="lg"
      keepMounted
      withCloseButton={false}
      styles={{
        body: { display: 'flex', flexDirection: 'column', height: '100%', padding: 'var(--mantine-spacing-md)' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
    >
      <Group justify="space-between" mb="md">
        <Text fw={600} size="lg">
          Upload Videos
          {activeCount > 0 && (
            <Text span size="sm" c="dimmed" ml="xs">
              ({activeCount} active)
            </Text>
          )}
        </Text>
        <Group gap={4}>
          <ActionIcon variant="subtle" color="gray" onClick={minimizeDrawer} aria-label="Minimize">
            <IconMinus size={16} />
          </ActionIcon>
          <ActionIcon variant="subtle" color="gray" onClick={closeDrawer} aria-label="Close">
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Group>

      <UploadDropzone
        addFilesWithMeta={addFilesWithMeta}
        ingestcfgs={ingestcfgs}
        selectedIngestcfgId={selectedIngestcfgId}
        onSelectIngestcfg={setSelectedIngestcfgId}
        hasMultiple={hasMultiple}
        isRestricted={isRestricted}
        cId={effectiveCId}
        hasFiles={items.length > 0}
      />

      <UploadFileList onCancel={cancelUpload} onRetry={retryUpload} />
    </Drawer>
  );
}
